import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import {
    getFacilityId,
    getParkerInitValues,
} from "../../Store/purchase.slice";
import {
    fetchBillingProfiles,
} from "../../Store/billingProfiles.slice";
import { HashRouter, Redirect, Route, Switch } from "react-router-dom";
import RatesPage from "./Pages/Rates";
import ParkerPage from "./Pages/Parker";
import SurveyPage from "./Pages/Survey";
import PayorPage from "./Pages/Payor";
import CheckoutPage from "./Pages/Checkout";
import ConfirmPage from "./Pages/Confirm";
import SubscriptionPage from "./Pages/Subscription";
import UnknownPage from "../Pages/UnknownPage";
import { nanoid } from "@reduxjs/toolkit";
import { Container } from "@material-ui/core";
import { createMuiTheme, ThemeProvider } from "@material-ui/core/styles"
import CssBaseline from "@material-ui/core/CssBaseline";
import AuthPage from "./Pages/Auth";
import { ConfirmProvider } from "material-ui-confirm";
import VersionNum from '../Common/VersionNum';
import { Authenticator } from '@aws-amplify/ui-react';
import {
    CognitoAccessToken, CognitoIdToken, CognitoRefreshToken, CognitoUser, CognitoUserPool,
    CognitoUserSession,
} from 'amazon-cognito-identity-js';
import jwt from 'jsonwebtoken';
import { useCookies } from 'react-cookie';
import { Auth } from 'aws-amplify';

const theme = createMuiTheme({
    typography: {
        fontFamily: [
            'FilsonSoft-Bold',
            'FilsonSoft-Heavy',
            '-apple-system',
            'sans-serif',
        ].join(','),
    },
    palette: {
        primary: { main: '#00AEEF' },
        secondary: { main: '#242424' }
    },
    overrides: {
        MuiCssBaseline: {
            '@global': {
                '.page-wrapper': {
                    margin: 0,
                    padding: 0,
                    borderBottom: '1px solid #BDC1CD'
                }
            }
        },
        MuiButton: {
            root: { textTransform: "none" },
            containedPrimary: { color: 'white' },
            containedSizeLarge: { fontSize: '1.5rem' }
        },
        MuiLinearProgress: {
            root: { height: '0.5rem' },
            colorPrimary: { backgroundColor: 'white' }
        }
    }
});

const requiredCookies = [
    'aws_cognito_id_token',
    'aws_cognito_access_token',
    'aws_cognito_refresh_token'
];

function loginWithCookies(
    cookies,
    userPool,
) {
    if (requiredCookies.every((awsCookie) => awsCookie in cookies)) {
        const decodedIdToken = jwt.decode(cookies.aws_cognito_id_token);
        if (decodedIdToken?.exp) {
            const currentTimeInSeconds = Math.floor(Date.now() / 1000);
            if (currentTimeInSeconds < decodedIdToken.exp) {
                const IdToken = new CognitoIdToken({ IdToken: cookies.aws_cognito_id_token });
                const AccessToken = new CognitoAccessToken({ AccessToken: cookies.aws_cognito_access_token });
                const RefreshToken = new CognitoRefreshToken({
                    RefreshToken: cookies.aws_cognito_refresh_token,
                });
                const Username = decodedIdToken['cognito:username'];
                const user = new CognitoUser({ Pool: userPool, Username });
                user.setSignInUserSession(new CognitoUserSession({
                    AccessToken,
                    IdToken,
                    RefreshToken,
                }));
            }
        }
    }
}

export async function handleSignIn(
    cookies,
    auth,
    setLoginLoading,
) {
    try {
        const userPool = new CognitoUserPool({
            UserPoolId: process.env.REACT_APP_AWS_USER_POOL_ID,
            ClientId: process.env.REACT_APP_AWS_APP_CLIENT_ID,
        });

        const currentUser = userPool.getCurrentUser();

        if (currentUser) {
            if (!requiredCookies.every((awsCookie) => awsCookie in cookies)) return;
            const decodedIdToken = jwt.decode(cookies.aws_cognito_id_token);
            const subFromCookies = decodedIdToken?.sub;
            const currentSession = await auth.currentSession();
            const subFromSession = currentSession?.getIdToken()?.payload?.sub;

            if (subFromCookies && subFromSession && subFromCookies !== subFromSession) {
                await auth.signOut();
                loginWithCookies(
                    cookies,
                    userPool,
                );
            }
        } else {
            loginWithCookies(
                cookies,
                userPool,
            );
        }
    } catch {
        console.log('Login failed');
    } finally {
        setLoginLoading(false);
    }
}

function Purchase() {
    const parkerInit = useSelector(getParkerInitValues);
    const facilityId = useSelector(getFacilityId);
    const dispatch = useDispatch();
    const promoCode = parkerInit.promoCode;
    const [cookies] = useCookies();
    const [loginLoading, setLoginLoading] = useState(false);


    async function dispatchBillingProfilesAndSaveBillingDay() {
        const map = await dispatch(fetchBillingProfiles({ facilityId, promoCode }));
        let idToBillingDay = {};
        if (map.payload?.billing_profiles) {
            Object.entries(map.payload?.billing_profiles)
                .filter(([, value]) => {
                    return !!value.secondMonthPayment
                })
                .forEach(([key, value]) => {
                    idToBillingDay[key] = value.billing_day;
                });
        }
    }

    useEffect(() => {
        dispatchBillingProfilesAndSaveBillingDay();
    }, [facilityId, promoCode, dispatch]);

    useEffect(() => {
        setLoginLoading(true);

        handleSignIn(
            cookies,
            Auth,
            setLoginLoading,
        );
    }, [cookies]);

    if(loginLoading) return null;

    const handler = () => facilityId ? <Redirect to={`/rates/${nanoid()}`} /> : <Redirect to='/error' />
    const display = (Page) => () => facilityId ?
        <Page /> :
        <Redirect to='/' />;
    return <ThemeProvider theme={theme}>
        <Authenticator.Provider>
            <ConfirmProvider defaultOptions={{
                confirmationButtonProps: { "data-cy": "confirm-dialog-confirmation-button" },
                cancellationButtonProps: { "data-cy": "confirm-dialog-cancel-button" }
            }}>
                <CssBaseline />
                <Container maxWidth="lg" disableGutters>
                    <HashRouter>
                        <Switch>
                            <Route exact path="/" render={handler} />
                            <Route exact path="/rates/:parkerId" render={display(RatesPage)} />
                            <Route exact path="/parker/:parkerId" render={display(ParkerPage)} />
                            <Route exact path="/survey" render={display(SurveyPage)} />
                            <Route exact path="/auth" render={display(AuthPage)} />
                            <Route exact path="/payor" render={display(PayorPage)} />
                            <Route exact path="/checkout" render={display(CheckoutPage)} />
                            <Route exact path="/confirm" render={display(ConfirmPage)} />
                            <Route exact path="/subscription" render={display(SubscriptionPage)} />
                            <Route exact path='/error' component={UnknownPage} />
                            <Route render={() => <Redirect to='/error' />} />
                        </Switch>
                    </HashRouter>
                </Container>
            </ConfirmProvider>
        </Authenticator.Provider>
        <VersionNum />
    </ThemeProvider>
}
export default React.memo(Purchase);
